import React, { useEffect, useMemo, useState } from 'react'
import { FormCheckboxField, FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  CreateRebateGroup,
  IntroducingBrokerRebateGroup,
} from 'api/Trading-Accounts/Types/IntroducingBrokerRebates'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { useAppSelector } from 'store'

interface Props {
  isOpen: boolean
  onClose(): void
  onSubmit?: (data: CreateRebateGroup) => void | Promise<void>
  onUpdate?: (data: CreateRebateGroup, id: string) => void | Promise<void>
  isEdit?: boolean
  data?: IntroducingBrokerRebateGroup
  id?: string
}

interface RebateGroupFormData {
  name: string
  rebateApprovalTypeId?: number
  rebatePeriodId?: number
  rebateAmountInPercentage: string
  isDefault: boolean
}

const createInitialData = (initialData?: IntroducingBrokerRebateGroup): RebateGroupFormData => ({
  name: initialData?.name ?? '',
  rebateApprovalTypeId: initialData?.rebateApprovalType?.id,
  rebatePeriodId: initialData?.rebatePeriod?.id,
  rebateAmountInPercentage:
    initialData?.rebateAmountInPercentage != null
      ? String(initialData.rebateAmountInPercentage)
      : '',
  isDefault: initialData?.isDefault ?? false,
})

export const CreateRebateGroupModal: React.FC<Props> = (props) => {
  const { onSubmit, onUpdate, isEdit = false, data: initialData, id, isOpen, onClose } = props
  const [data, setData] = useState<RebateGroupFormData>(() => createInitialData(initialData))
  const [confirmModal, setConfirmModal] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    setData(createInitialData(initialData))
    setConfirmModal(false)
  }, [isOpen, initialData])

  const { ibRebatesApprovalTypes, ibRebatesPeriods } = useAppSelector(
    ({ partnerships }) => partnerships
  )

  const rebateAmountError = useMemo(() => {
    if (data.rebateAmountInPercentage.trim() === '') return undefined

    const rebateAmount = Number(data.rebateAmountInPercentage)

    if (Number.isNaN(rebateAmount)) {
      return 'Rebate amount must be a number.'
    }

    if (rebateAmount < 0) {
      return 'Rebate amount must be 0 or more.'
    }

    if (rebateAmount > 100) {
      return 'Rebate amount must be 100 or less.'
    }

    return undefined
  }, [data.rebateAmountInPercentage])

  const isValid = useMemo(
    () =>
      Boolean(data.name.trim()) &&
      data.rebateApprovalTypeId !== undefined &&
      data.rebatePeriodId !== undefined &&
      data.rebateAmountInPercentage.trim() !== '' &&
      !rebateAmountError,
    [data, rebateAmountError]
  )

  const handleSubmit = async () => {
    const name = data.name.trim()
    const rebateAmount = Number(data.rebateAmountInPercentage)

    if (!name) return
    if (data.rebateApprovalTypeId === undefined) return
    if (data.rebatePeriodId === undefined) return
    if (data.rebateAmountInPercentage.trim() === '' || rebateAmountError) return
    if (Number.isNaN(rebateAmount)) return

    setConfirmModal(false)

    const payload: CreateRebateGroup = {
      name,
      rebateApprovalTypeId: data.rebateApprovalTypeId,
      rebatePeriodId: data.rebatePeriodId,
      rebateAmountInPercentage: rebateAmount,
      isDefault: data.isDefault,
    }

    if (isEdit && id && onUpdate) {
      await onUpdate(payload, id)
      return
    }

    if (onSubmit) {
      await onSubmit(payload)
    }
  }

  return (
    <>
      <ConfirmationModal
        contentText={
          isEdit
            ? 'Are you sure you want to edit the group?'
            : 'Are you sure you want to create the group?'
        }
        yesButtonText='Yes'
        cancelButtonText='No'
        onYes={handleSubmit}
        onCancel={() => setConfirmModal(false)}
        visible={confirmModal}
      />

      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit Rebate Group' : 'Create New Group'}</DialogTitle>
          </DialogHeader>

          <FormField
            name='name'
            label='Group name'
            value={data.name}
            onChange={(event) => setData((current) => ({ ...current, name: event.target.value }))}
          />
          <FormSelectField
            name='rebateApprovalTypeId'
            label='Type'
            options={ibRebatesApprovalTypes}
            value={data.rebateApprovalTypeId}
            onChange={(value) =>
              setData((current) => ({
                ...current,
                rebateApprovalTypeId: Number(value),
              }))
            }
          />
          <FormSelectField
            name='rebatePeriodId'
            label='Payout period'
            options={ibRebatesPeriods}
            value={data.rebatePeriodId}
            onChange={(value) =>
              setData((current) => ({
                ...current,
                rebatePeriodId: Number(value),
              }))
            }
          />
          <FormField
            name='rebateAmountInPercentage'
            label='Rebate amount, %'
            type='number'
            value={data.rebateAmountInPercentage}
            error={rebateAmountError}
            onChange={(event) =>
              setData((current) => ({
                ...current,
                rebateAmountInPercentage: event.target.value,
              }))
            }
          />
          <FormCheckboxField
            name='isDefault'
            label='Default'
            value={data.isDefault}
            onChange={(value) => setData((current) => ({ ...current, isDefault: value }))}
          />

          <DialogFooter>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => setConfirmModal(true)}
              disabled={!isValid}
              data-test='add-rebate-button'
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
